<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import CustomerDialog from '@/components/core/customers/CustomerDialog.vue'
import CustomerProfileContactDetails from '@/components/core/customers/CustomerProfileContactDetails.vue'
import { useCustomers } from '@/composables/core/customers'
import { useOIDCStore } from '@/stores/oidc.store'
import { useRepairActionStore } from '@/stores/repair-action.store'
import { useRepairServiceStore } from '@/stores/repair-service.store'
import DetailsViewLayout from '@/views/layouts/DetailsViewLayout.vue'
import { Icon } from '@iconify/vue'
import { type TabsPaneContext } from 'element-plus'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const oidcStore = useOIDCStore()
const { isRcAppManagerUser, isASICWManagerUser } = storeToRefs(oidcStore)

// Populate services and repair actions list as it is used in rc-scope-of-work and create-repair-request-dialog.
const repairServiceStore = useRepairServiceStore()
repairServiceStore.getRepairServices()

const repairActionsStore = useRepairActionStore()
repairActionsStore.getRepairActions()

const router = useRouter()

const getActiveNameFromRoute = () => {
  const entityLevelFragmentName = router.currentRoute.value.matched[3]?.name as string
  return entityLevelFragmentName?.split('-')[0] || ''
}

const activeName = ref(getActiveNameFromRoute())

// Watch for route changes to keep activeName in sync
watch(
  () => router.currentRoute.value.matched[3]?.name,
  (newName) => {
    if (newName) {
      activeName.value = (newName as string).split('-')[0]
    }
  },
  { immediate: true }
)

const props = defineProps<{
  customerId: number
}>()

const contactDetailsVisible = ref(false)
const customerDialogVisible = ref(false)

const customerId = computed(() => props.customerId)

const { getSingleCustomerQuery, deleteCustomerMutation } = useCustomers()
const { data: customer } = getSingleCustomerQuery(customerId.value)

const onEditCustomer = () => {
  customerDialogVisible.value = true
}

const onDeleteCustomer = () => {
  DeleteConfirmationModal(
    'Delete Customer',
    'Are you sure you want to delete this customer? This action cannot be undone.',
    customer.value?.id,
    deleteCustomerMutation.mutateAsync,
    () => {
      router.push({ name: 'core-customers' })
    }
  )
}

const onTabClick = ({ paneName }: TabsPaneContext) => {
  router.push({ name: `${paneName}-customer` })
}
</script>

<template>
  <details-view-layout back-button-text="Back to previous screen" @on-back-click="router.back()">
    <el-dialog v-model="contactDetailsVisible" class="w-max" title="All info">
      <customer-profile-contact-details v-if="customer" :customer-id="customer.id" />
    </el-dialog>
    <customer-dialog v-if="customer" v-model="customerDialogVisible" :customer-id="customer.id" />
    <el-container>
      <el-header class="flex items-center gap-6">
        <el-avatar :size="100" class="text-3xl bg-gray color-white">{{ customer?.first_name?.[0] }}</el-avatar>
        <div class="flex flex-col items-start gap-1">
          <span class="text-xl font-medium">
            {{ customer?.first_name }} {{ customer?.last_name }}
            {{ customer?.company_name ? `(${customer?.company_name})` : '' }}
          </span>
          <div class="flex gap-4 items-center text-sm text-text-secondary">
            <span v-if="customer?.phone" class="flex gap-2 items-center"
              ><Icon icon="mdi:phone-outline" /> {{ customer?.phone }}</span
            >
            <span class="flex gap-2 items-center"><Icon icon="mdi:envelope-outline" /> {{ customer?.email }}</span>
          </div>
          <el-link class="text-sm text-text-tertiary font-medium" @click="contactDetailsVisible = true">
            View all info
          </el-link>
        </div>
        <div class="ml-auto">
          <el-button size="large" @click="onEditCustomer()">
            <Icon icon="mdi:pencil-outline" class="mr-2" />Edit
          </el-button>
          <el-button size="large" @click="onDeleteCustomer()"><Icon icon="mdi:trash-can-outline" /></el-button>
        </div>
      </el-header>
      <el-main>
        <el-tabs v-model="activeName" class="demo-tabs" @tab-click="onTabClick">
          <el-tab-pane v-if="isRcAppManagerUser" label="Repair Center" name="rc" />
          <el-tab-pane v-if="isASICWManagerUser" label="ASIC Assure" name="asicw" />
          <router-view />
        </el-tabs>
      </el-main>
    </el-container>
  </details-view-layout>
</template>
